import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { CloseIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Button } from '@/components/ui/Button/Button';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import {
  mergeHistoryPage,
  projectHistoryQueryKey,
  useProjectChangeHistory,
} from './projectChangeHistoryQuery';
import { HistoryGroupRow } from './HistoryGroupRow';
import { groupHistoryByChangeSet } from './groupHistoryByChangeSet';
import type { ProjectChangeHistoryEntry, ProjectChangeHistoryPage } from './types';
import './ProjectChangeHistoryPopover.css';

/** `below-cell` — под ячейкой; `beside-menu` — слева от кнопки/меню, по центру высоты якоря */
export type ProjectChangeHistoryAnchorPlacement = 'below-cell' | 'beside-menu';

export interface ProjectChangeHistoryPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  /** Откуда открыли попап — влияет на позиционирование относительно якоря */
  anchorPlacement?: ProjectChangeHistoryAnchorPlacement;
  projectId: string;
  projectName?: string;
  columnId?: string;
  /** Client-side filter for combo cells (multiple column ids) */
  columnIds?: string[];
  title?: string;
}

function filterHistoryItems(
  items: ProjectChangeHistoryEntry[],
  columnId?: string,
  columnIds?: string[]
): ProjectChangeHistoryEntry[] {
  if (columnId) return items;
  if (!columnIds?.length) return items;
  const allowed = new Set(columnIds);
  return items.filter((item) => item.column_id != null && allowed.has(item.column_id));
}

export function ProjectChangeHistoryPopover({
  open,
  onClose,
  anchorEl,
  anchorPlacement = 'below-cell',
  projectId,
  projectName,
  columnId,
  columnIds,
  title,
}: ProjectChangeHistoryPopoverProps) {
  const queryClient = useQueryClient();
  const popoverRef = useRef<HTMLDivElement>(null);
  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(open, onClose, true);
  const fetchColumnId = columnIds?.length ? undefined : columnId;
  const { data, isPending, isError, error } = useProjectChangeHistory(projectId, {
    columnId: fetchColumnId,
    enabled: shouldRender,
  });
  const displayGroups = useMemo(() => {
    const filtered = filterHistoryItems(data?.items ?? [], columnId, columnIds);
    return groupHistoryByChangeSet(filtered);
  }, [columnId, columnIds, data?.items]);

  const [loadingMore, setLoadingMore] = useState(false);

  const besideMenu = anchorPlacement === 'beside-menu';

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    horizontalAlign: 'center',
    verticalAlign: besideMenu ? 'center' : 'start',
    priorityAxis: besideMenu ? 'x' : 'y',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1100,
    clampOnScrollFollow: !besideMenu,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    enabled: shouldRender,
  });

  const heading =
    columnId || columnIds?.length
      ? `Изменения: ${title?.trim() || 'Ячейка'}`
      : title ??
        (projectName
          ? `Изменения: ${projectName}`
          : 'Изменения проекта');

  const loadMore = useCallback(async () => {
    if (!data?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await api.projects.getChanges(projectId, {
        columnId: fetchColumnId,
        before_cursor: data.next_cursor,
      });
      const key = projectHistoryQueryKey(projectId, fetchColumnId);
      queryClient.setQueryData<ProjectChangeHistoryPage>(key, (prev) =>
        mergeHistoryPage(prev, next)
      );
    } finally {
      setLoadingMore(false);
    }
  }, [fetchColumnId, data?.next_cursor, loadingMore, projectId, queryClient]);

  if (!shouldRender) return null;

  return createPortal(
    <div ref={popoverRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}>
      <OverlayIntrinsicSize>
        <div
          className={`project-change-history-popover ${contentClasses}`}
          role="dialog"
          aria-label={heading}
        >
          <div className="project-change-history-popover__header">
            <h2 className="project-change-history-popover__title">{heading}</h2>
            <IconButton
              type="button"
              variant="danger"
              size="m"
              iconSize={20}
              className="project-change-history-popover__close"
              aria-label="Закрыть"
              onClick={requestClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="project-change-history-popover__body">
            <OverlayScrollArea className="project-change-history-popover__scroll">
              <div className="project-change-history-popover__scroll-pad">
                {isPending && (
                  <div
                    className="project-change-history-popover__loading"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    <span
                      className="project-change-history-popover__loading-spinner"
                      aria-label="Загрузка истории"
                    />
                  </div>
                )}
                {isError && (
                  <p className="project-change-history-popover__empty">
                    {(error as Error)?.message ?? 'Не удалось загрузить историю'}
                  </p>
                )}
                {!isPending && !isError && (
                  <ul className="project-change-history-popover__list" role="list">
                    {displayGroups.length === 0 ? (
                      <li className="project-change-history-popover__empty">
                        <i
                          className="fa-solid fa-hourglass-half project-change-history-popover__empty-icon"
                          aria-hidden
                        />
                        Изменений пока нет
                      </li>
                    ) : (
                      displayGroups.map((group) => (
                        <HistoryGroupRow key={group.group_key} group={group} />
                      ))
                    )}
                  </ul>
                )}
              </div>
            </OverlayScrollArea>
            {!isPending && !isError && data?.next_cursor && (
              <div className="project-change-history-popover__footer">
                <Button
                  type="button"
                  variant="secondary"
                  size="s"
                  className="project-change-history-popover__load-more"
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                >
                  {loadingMore ? (
                    'Загрузка…'
                  ) : (
                    <>
                      <i className="fa-solid fa-chevron-up" aria-hidden /> Показать раньше
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
